import {Injectable} from '@angular/core';
import {
    HttpResponse,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
} from '@angular/common/http';
import {Observable, Subject} from 'rxjs';
import {take} from 'rxjs/operators';
import {LoaderService} from '@services/loader.service';
import {CommonService} from '@services/common.service';
import {environment} from '@environments/environment';
import {AuthService} from '@services/auth.service';

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {
    private requests: HttpRequest<any>[] = [];
    private isRefreshing = false;
    private refreshSubject = new Subject<boolean>();

    constructor(
        private authService: AuthService,
        private commonService: CommonService,
        private loaderService: LoaderService,
    ) {
    }

    removeRequest(req: HttpRequest<any>): void {
        const i = this.requests.indexOf(req);

        if (i >= 0) {
            this.requests.splice(i, 1);
            this.loaderService.isLoading.next(this.requests.length > 0);
        }
    }

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<any>> {
        const buildName: string = sessionStorage.getItem('buildName') || '';

        // default headers
        const setHeaders = {
            DevUserId: `${environment.devUserId}`,
            buildName,
            Accept: 'application/json',
            'Access-Control-Expose-Headers': 'Set-Cookie',
            Authorization: '',
        };

        const accessToken = this.authService.getSessionAccessToken();

        // If the request does not have the Authorization header, add our token
        if (!req.headers.has('Authorization')) {
            setHeaders.Authorization = `Bearer ${accessToken}`;
        } else {
            setHeaders.Authorization = req.headers.get('Authorization') || `Bearer ${accessToken}`;
        }

        // Clone the request to add the new header
        req = req.clone({
            withCredentials: true,
            setHeaders,
        });

        this.requests.push(req);

        if (!req.url.includes('formatters')) {
            this.loaderService.isLoading.next(true);
        }

        return new Observable((observer: any) => {
            const subscription = next.handle(req).subscribe({
                next: (event) => {
                    if (event instanceof HttpResponse) {
                        this.removeRequest(req);
                        this.loaderService.isLoading.next(false);
                        observer.next(event);
                    }
                },
                error: (error) => {
                    this.removeRequest(req);
                    this.loaderService.isLoading.next(false);

                    if (!(error instanceof HttpErrorResponse)) {
                        observer.error(error);
                        return;
                    }

                    const errorMessage = error.error?.message || '';
                    const url = error.url || '';
                    const reportNotFound = error.error?.reportNotFound || false;

                    if (error.status === 401 || error.status === 403) {
                        // Expired access token on a non-session endpoint — refresh and retry
                        if (
                            (errorMessage.includes('Expired token') || errorMessage.includes('Invalid token')) &&
                            !url.includes('/sessions/')
                        ) {
                            this.refreshTokenAndRetry(req, next, observer, error);
                            return;
                        }

                        if (errorMessage.includes('Session expired')) {
                            this.authService.logout();
                            return;
                        }

                        observer.error(error);
                        return;
                    }

                    observer.error(error);

                    if (url.includes('preview')) {
                        const previewErrorMessage =
                            error.error?.message || 'An error occurred on the server.';
                        const errAddDetails =
                            error.error?.additional_data || 'No additional details provided.';

                        if (errAddDetails.type == 'shared-field') {
                            const dialogRef = this.commonService.openErrorDialog(
                                errAddDetails.type,
                                previewErrorMessage,
                                '',
                            );

                            // Await the closure of the dialog and subsequently initiate the event to remove the shared data field, allowing for its re-addition in sequence with both upstream and downstream collections, thereby resolving the 500 internal server error.
                            dialogRef?.afterClosed().subscribe(() => {
                                if (errAddDetails.id) {
                                    this.commonService.triggerRemoveDataField(errAddDetails.id);
                                }
                            });
                        } else {
                            this.commonService.openErrorDialog(
                                'system',
                                'The last successful saved instance has been loaded',
                                '',
                            );
                        }
                    } else if (url.includes('download')) {
                        console.log(
                            'While exporting a report, an exception should be thrown with a proper message in case of non handled broken HTML for the end user instead of displaying a system error.',
                        );
                    } else {
                        console.error(error?.error?.exception ?? error?.message);
                        console.error(error?.error?.message ?? '');

                        if (reportNotFound) {
                            this.commonService.openErrorDialog(
                                'canvas-not-found',
                                'This report has been deleted; please contact an administrator.',
                                '',
                            );
                        } else {
                            this.commonService.openErrorDialog('system', '', '');
                        }
                    }
                },
                complete: () => {
                    this.removeRequest(req);
                    this.loaderService.isLoading.next(false);
                    observer.complete();
                },
            });

            // remove request from queue when cancelled
            return () => {
                this.removeRequest(req);
                this.loaderService.isLoading.next(false);
                subscription.unsubscribe();
            };
        });
    }

    private refreshTokenAndRetry(
        req: HttpRequest<any>,
        next: HttpHandler,
        observer: any,
        originalError: HttpErrorResponse,
    ): void {
        if (this.isRefreshing) {
            // Another request is already refreshing — queue behind it
            this.refreshSubject.pipe(take(1)).subscribe({
                next: (success) => {
                    if (success) {
                        this.retryWithNewToken(req, next, observer);
                    } else {
                        observer.error(originalError);
                    }
                },
            });
            return;
        }

        this.isRefreshing = true;

        this.authService.refreshSession().subscribe({
            next: () => {
                this.isRefreshing = false;
                this.refreshSubject.next(true);
                this.retryWithNewToken(req, next, observer);
            },
            error: () => {
                this.isRefreshing = false;
                this.refreshSubject.next(false);
                observer.error(originalError);
            },
        });
    }

    private retryWithNewToken(
        req: HttpRequest<any>,
        next: HttpHandler,
        observer: any,
    ): void {
        const retryReq = req.clone({
            setHeaders: {
                Authorization: `Bearer ${this.authService.getSessionAccessToken()}`,
            },
        });

        next.handle(retryReq).subscribe({
            next: (event) => observer.next(event),
            error: (err) => observer.error(err),
            complete: () => observer.complete(),
        });
    }
}
